import { useEffect, useRef, useState } from 'react'
import { Upload, Loader2, RefreshCw } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../lib/shadcn/card'
import { Button } from '../../lib/shadcn/button'
import { toast } from '../../lib/shadcn/sonner'
import {
  useImportAvailableSkus,
  useGetAvailableSkuStats,
} from '../../hooks/backend/inventoryPage'
import { HelpText } from '../../components/HelpText'

interface SkuStats {
  total: number
  available: number
  used: number
}

interface ImportResult {
  received: number
  inserted: number
  duplicates: number
  total_in_pool: number
  available_in_pool: number
}

export default function AvailableSkusSettings() {
  const importSkus = useImportAvailableSkus()
  const getStats = useGetAvailableSkuStats()
  const fileInputRef = useRef<HTMLInputElement>(null)

  const stats = (getStats.data as SkuStats | undefined) ?? { total: 0, available: 0, used: 0 }

  useEffect(() => {
    void getStats.trigger({})
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [uploading, setUploading] = useState(false)

  const handleFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    // Reset the input so the same file can be re-selected later.
    if (fileInputRef.current) fileInputRef.current.value = ''
    if (!file) return

    if (!/\.csv$/i.test(file.name) && file.type !== 'text/csv') {
      toast.error('Please choose a .csv file')
      return
    }

    setUploading(true)
    try {
      const csv = await file.text()
      const result = (await importSkus.trigger({ csv })) as ImportResult
      toast.success(
        `Imported ${result.inserted} new SKU${result.inserted === 1 ? '' : 's'}` +
          (result.duplicates > 0 ? ` (${result.duplicates} already in pool)` : '')
      )
      void getStats.trigger({}, { skipCache: true })
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to import SKUs')
    } finally {
      setUploading(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Available SKUs</CardTitle>
        <CardDescription><HelpText>
          Upload a CSV of SKUs to add to the pool. New products auto-fill their SKU from this pool.
          One SKU per row (first column); a &quot;sku&quot; header row is optional.
        </HelpText></CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-3 gap-3">
          {([
            { label: 'Available', value: stats.available },
            { label: 'Used', value: stats.used },
            { label: 'Total', value: stats.total },
          ] as const).map(s => (
            <div key={s.label} className="flex flex-col items-center rounded-md border bg-background p-3">
              <span className="text-xs uppercase tracking-wide text-muted-foreground">{s.label}</span>
              <span className="mt-1 text-2xl font-bold text-foreground">{s.value}</span>
            </div>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            onChange={e => void handleFile(e)}
          />
          <Button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="gap-1.5"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {uploading ? 'Importing…' : 'Upload CSV'}
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => void getStats.trigger({}, { skipCache: true })}
            disabled={getStats.loading}
            aria-label="Refresh SKU counts"
          >
            <RefreshCw className={`h-4 w-4 ${getStats.loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}
